import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../App";
import apiClient from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AlertTriangle, Calendar, Loader2, Clock, Package, AlertCircle } from "lucide-react";
import { toastApiError } from "../../lib/toastApiError";

export default function ExpiringProducts() {
  const { t, i18n } = useTranslation("pharmacy");
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const dateLocale = i18n.language === "en" ? "en-US" : "fr-FR";

  const fetchExpiringProducts = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/pharmacy/expiring-products?days=${days}`);
      setData(res.data);
    } catch (error) {
      console.error("Error fetching expiring products:", error);
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpiringProducts();
  }, [days]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(dateLocale, { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return dateStr;
    }
  };

  const getExpiryBadge = (daysUntil, isExpired) => {
    if (isExpired) {
      return <Badge className="bg-red-500 text-white">{t("expiring.expiredBadge")}</Badge>;
    }
    if (daysUntil <= 7) {
      return <Badge className="bg-red-100 text-red-700">{t("expiring.expiresIn", { days: daysUntil })}</Badge>;
    }
    if (daysUntil <= 30) {
      return <Badge className="bg-amber-100 text-amber-700">{t("expiring.expiresIn", { days: daysUntil })}</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-700">{t("expiring.daysCount", { days: daysUntil })}</Badge>;
  };

  const expiredCount = data?.items?.filter(i => i.is_expired).length || 0;
  const criticalCount = data?.items?.filter(i => !i.is_expired && i.days_until_expiry <= 7).length || 0;
  const warningCount = data?.items?.filter(i => !i.is_expired && i.days_until_expiry > 7 && i.days_until_expiry <= 30).length || 0;
  const noDateCount = data?.total_no_date || 0;

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="expiring-products-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("expiring.title")}</h1>
          <p className="text-slate-500 mt-1">{t("expiring.subtitle")}</p>
        </div>

        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[200px]" data-testid="days-select">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">{t("expiring.in7days")}</SelectItem>
            <SelectItem value="30">{t("expiring.in30days")}</SelectItem>
            <SelectItem value="60">{t("expiring.in60days")}</SelectItem>
            <SelectItem value="90">{t("expiring.in90days")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-omu-blue-500" />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className={expiredCount > 0 ? "border-red-300 bg-red-50" : ""}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${expiredCount > 0 ? "bg-red-100" : "bg-slate-100"}`}>
                    <AlertCircle className={`h-6 w-6 ${expiredCount > 0 ? "text-red-600" : "text-slate-400"}`} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("expiring.expired")}</p>
                    <p className={`text-2xl font-bold ${expiredCount > 0 ? "text-red-600" : "text-slate-400"}`}>
                      {expiredCount}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={criticalCount > 0 ? "border-amber-300 bg-amber-50" : ""}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${criticalCount > 0 ? "bg-amber-100" : "bg-slate-100"}`}>
                    <AlertTriangle className={`h-6 w-6 ${criticalCount > 0 ? "text-amber-600" : "text-slate-400"}`} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("expiring.critical")}</p>
                    <p className={`text-2xl font-bold ${criticalCount > 0 ? "text-amber-600" : "text-slate-400"}`}>
                      {criticalCount}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Clock className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{t("expiring.toWatch")}</p>
                    <p className="text-2xl font-bold text-blue-600">{warningCount}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5 text-omu-blue-500" />
                {t("expiring.listTitle", { days, count: data?.total || 0 })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.items && data.items.length > 0 ? (
                <div className="space-y-3">
                  {data.items.map((item, index) => (
                    <div
                      key={item.id || index}
                      className={`flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-lg border ${
                        item.is_expired
                          ? "bg-red-50 border-red-200"
                          : item.days_until_expiry <= 7
                          ? "bg-amber-50 border-amber-200"
                          : "bg-slate-50 border-slate-200"
                      }`}
                      data-testid={`expiring-item-${index}`}
                    >
                      <div className="flex-1">
                        <h3 className="font-medium text-slate-900">
                          {item.medicine?.name || t("expiring.unknownProduct")}
                        </h3>
                        <p className="text-sm text-slate-500">
                          {item.medicine?.dosage} • {t("expiring.quantity", { qty: item.quantity })}
                        </p>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-sm text-slate-600">{t("expiring.expiresOn")}</p>
                          <p className="font-medium">{formatDate(item.expiry_date)}</p>
                        </div>
                        {getExpiryBadge(item.days_until_expiry, item.is_expired)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Package className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p>{t("expiring.empty")}</p>
                  <p className="text-sm mt-2">{t("expiring.emptyGood")}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {(expiredCount > 0 || criticalCount > 0) && (
            <Card className="bg-gradient-to-r from-red-50 to-amber-50 border-red-200">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-red-900">{t("expiring.actionRequired")}</h3>
                    <p className="text-sm text-red-700 mt-1">
                      {t("expiring.actionDesc", { count: expiredCount + criticalCount })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {noDateCount > 0 && (
            <Card className="border-amber-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="h-5 w-5" />
                  {t("expiring.noDateTitle", { count: noDateCount })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-amber-600 mb-4">
                  {t("expiring.noDateDesc")}
                </p>
                <div className="space-y-2">
                  {data.no_date_items?.map((item, index) => (
                    <div
                      key={item.id || `nodate-${index}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-100"
                    >
                      <div>
                        <p className="font-medium text-slate-900">{item.medicine?.name || t("expiring.unknownProduct")}</p>
                        <p className="text-sm text-slate-500">{item.medicine?.dosage} - {t("expiring.qtyShort", { qty: item.quantity })}</p>
                      </div>
                      <Badge className="bg-amber-100 text-amber-700">{t("expiring.missingDate")}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
